import React from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import styled from 'styled-components';
import { H1, H2, Icon } from 'components';
import { lineHeight, theme, toRem } from 'design';

export const SummaryLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space['2xl'])};
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(theme.space['3xl'])};
  }
`;

const MAX_WIDTH = 600;

export const SummaryGrid = styled.section`
  display: grid;
  grid-template-columns:
    ${toRem(theme.space.xl)}
    1fr
    ${toRem(theme.space.xl)};
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-template-columns:
      ${toRem(theme.space['3xl'])}
      1fr
      ${toRem(theme.space['3xl'])};
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    grid-template-columns:
      minmax(${toRem(theme.space['3xl'])}, 1fr)
      minmax(0, ${toRem(MAX_WIDTH)})
      minmax(${toRem(theme.space['3xl'])}, 1fr);
  }
`;

export const SummaryContent = styled.div`
  grid-column: 2/3;
`;

const HeaderLayout = styled(SummaryGrid).attrs({ as: 'header' })`
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  background-color: ${theme.color.neutral[100]};
`;

const HeaderContent = styled(SummaryContent)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.lg)};
  align-items: center;
  width: 100%;
  padding: ${toRem(theme.space['2xl'])} 0;
  text-align: center;

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-template-columns: auto 1fr;
    grid-gap: ${toRem(theme.space['2xl'])};
    padding: ${toRem(theme.space['3xl'])} 0;
    text-align: left;
  }
`;

const IconContainer = styled.div`
  position: relative;
  left: ${toRem(11)};
  justify-content: center;
  align-items: center;
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    left: 0;
  }
`;

const HeaderIcon = styled(Icon).attrs({ icon: 'PaymentSummary' })`
  width: ${toRem(77)};
  height: auto;

  @media (min-width: ${theme.breakpoint.med}px) {
    width: ${toRem(110)};
  }
`;

const HeaderMessaging = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    align-items: flex-start;
  }

  h1 {
    width: 100%;
    margin-bottom: ${toRem(theme.space.xs)};
  }

  h2 {
    width: 100%;
    margin-bottom: ${toRem(theme.space.sm)};
  }
`;

const MessageList = styled.ul`
  max-width: ${toRem(MAX_WIDTH * 0.75)};
  padding-left: ${toRem(theme.space.lg)};
  text-align: left;
`;

const MessageListItem = styled.li`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  width: 100%;
`;

// Loaders
const LoaderHeaderIcon = styled(Skeleton)`
  && {
    width: ${toRem(77)};
    height: ${toRem(77)};
    border-radius: 50%;

    @media (min-width: ${theme.breakpoint.med}px) {
      width: ${toRem(110)};
      height: ${toRem(110)};
    }
  }
`;

interface IChildren {
  children?: React.ReactNode;
  isLoading?: boolean;
}

interface ISummaryHeaderProps {
  totalNet: string;
  isLoading?: boolean;
}

export const SummaryHeader = ({ totalNet, isLoading }: ISummaryHeaderProps) => (
  <SkeletonTheme color={theme.color.neutral[100]} highlightColor={theme.color.neutral[100]}>
    <HeaderLayout data-cy="payment-summary-header">
      <HeaderContent>
        <IconContainer>{isLoading ? <LoaderHeaderIcon /> : <HeaderIcon />}</IconContainer>

        <HeaderMessaging>
          <H1>{isLoading ? <Skeleton /> : 'Payment Complete!'}</H1>
          <H2>{isLoading ? <Skeleton /> : `Payments totaling ${totalNet} have been submitted for processing`}</H2>
          {isLoading ? (
            <Skeleton count={3} />
          ) : (
            <MessageList>
              <MessageListItem>Text or email notifications have been sent to all recipients</MessageListItem>
              <MessageListItem>Instant payments have been submitted to the bank and should post momentarily</MessageListItem>
              <MessageListItem>Payroll payments have been recorded for submission to payroll</MessageListItem>
            </MessageList>
          )}
        </HeaderMessaging>
      </HeaderContent>
    </HeaderLayout>
  </SkeletonTheme>
);

export const SummaryDescription = styled.li`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.md)};
  width: 100%;
  margin-top: ${toRem(theme.space.xl)};
  color: ${theme.color.neutral[900]};
  word-break: break-word;
`;

const FooterContent = styled(SummaryContent)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, ${toRem(210)}));
  grid-gap: ${toRem(theme.space.md)};
  justify-content: center;

  > * {
    width: 100%;
    padding: 0 0;
  }
`;

const LoaderButton = styled(Skeleton).attrs({
  height: 44,
})`
  && {
    border-radius: ${toRem(22)};
  }
`;

export const SummaryFooter = ({ children, isLoading }: IChildren) => (
  <SummaryGrid as="footer">
    <FooterContent>
      {isLoading ? (
        <>
          <LoaderButton />
          <LoaderButton />
        </>
      ) : (
        children
      )}
    </FooterContent>
  </SummaryGrid>
);
